import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import patientService from '../../../../services/patient.service';
import articlesService from '../../../../services/articles.service';
import Pagination from '../../../common/Pagination';
import { showError, showSuccess } from '../../../../utils/ecxSwal';

const DEFAULT_CUSTOM_MESSAGE = 'Here is an interesting article for you to read.';
const PAGE_SIZE = 8;
const PREVIEW_DEBOUNCE_MS = 400;

function flattenCategories(categories, parentId = 0, depth = 0) {
    const result = [];

    categories
        .filter((category) => category.parentCategoryId === parentId)
        .forEach((category) => {
            result.push({ ...category, depth });
            result.push(...flattenCategories(categories, category.id, depth + 1));
        });

    return result;
}

function SendPatientArticlesModal({ show, onClose, clinicId, patient }) {
    const [categories, setCategories] = useState([]);
    const [articles, setArticles] = useState([]);
    const [articlesLoading, setArticlesLoading] = useState(false);
    const [pageNo, setPageNo] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [categoryId, setCategoryId] = useState(0);
    const [searchTitle, setSearchTitle] = useState('');
    const [activeSearchTitle, setActiveSearchTitle] = useState('');
    const [selectedArticleIds, setSelectedArticleIds] = useState([]);
    const [selectedArticlesById, setSelectedArticlesById] = useState({});
    const [customMessage, setCustomMessage] = useState(DEFAULT_CUSTOM_MESSAGE);
    const [previewHtml, setPreviewHtml] = useState('');
    const [previewSubject, setPreviewSubject] = useState('');
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(false);
    const [sending, setSending] = useState(false);

    const patientLabel = useMemo(() => {
        if (!patient) {
            return '';
        }

        const name = [patient.firstName, patient.lastName].filter(Boolean).join(' ');
        return patient.preferredName ? `${name} (${patient.preferredName})` : name;
    }, [patient]);

    const flatCategories = useMemo(() => flattenCategories(categories), [categories]);

    const resetState = useCallback(() => {
        setPageNo(0);
        setCategoryId(0);
        setSearchTitle('');
        setActiveSearchTitle('');
        setSelectedArticleIds([]);
        setSelectedArticlesById({});
        setCustomMessage(DEFAULT_CUSTOM_MESSAGE);
        setPreviewHtml('');
        setPreviewSubject('');
        setPreviewError(false);
        setPreviewLoading(false);
        setSending(false);
    }, []);

    const fetchCategories = useCallback(async () => {
        if (!clinicId) {
            return;
        }

        try {
            const res = await articlesService.getCategories(clinicId);
            setCategories(res.data || []);
        } catch (error) {
            console.error('Failed to load article categories', error);
        }
    }, [clinicId]);

    const fetchArticles = useCallback(async () => {
        if (!clinicId) {
            return;
        }

        setArticlesLoading(true);

        try {
            const res = await articlesService.getArticles({
                clinicId,
                pageNo,
                pageSize: PAGE_SIZE,
                sortBy: 'lastModified',
                order: 'desc',
                categoryId,
                title: activeSearchTitle,
            });

            setArticles(res.data.content || []);
            setTotalPages(res.data.totalPages || 0);
            setTotalElements(res.data.totalElements || 0);
        } catch (error) {
            console.error('Failed to load articles', error);
            showError({
                title: 'Load Failed',
                text: 'Could not load articles for selection.',
            });
        } finally {
            setArticlesLoading(false);
        }
    }, [clinicId, pageNo, categoryId, activeSearchTitle]);

    useEffect(() => {
        if (show) {
            resetState();
            fetchCategories();
        }
    }, [show, resetState, fetchCategories]);

    useEffect(() => {
        if (show) {
            fetchArticles();
        }
    }, [show, fetchArticles]);

    const toggleArticleSelection = (article) => {
        if (article.status === false) {
            return;
        }

        setSelectedArticleIds((prev) => {
            if (prev.includes(article.id)) {
                return prev.filter((id) => id !== article.id);
            }

            return [...prev, article.id];
        });

        setSelectedArticlesById((prev) => {
            const next = { ...prev };

            if (next[article.id]) {
                delete next[article.id];
            } else {
                next[article.id] = article;
            }

            return next;
        });
    };

    const clearSelection = () => {
        setSelectedArticleIds([]);
        setSelectedArticlesById({});
    };

    const handleSearchSubmit = (event) => {
        event.preventDefault();
        setActiveSearchTitle(searchTitle.trim());
        setPageNo(0);
    };

    const handleCategoryChange = (event) => {
        setCategoryId(Number(event.target.value));
        setPageNo(0);
    };

    const buildPayload = useCallback(
        () => ({
            articleIds: selectedArticleIds,
            customMessage: customMessage.trim() || DEFAULT_CUSTOM_MESSAGE,
        }),
        [selectedArticleIds, customMessage]
    );

    const loadPreview = useCallback(async () => {
        if (!clinicId || !patient?.id || selectedArticleIds.length === 0) {
            setPreviewHtml('');
            setPreviewSubject('');
            setPreviewError(false);
            return;
        }

        setPreviewLoading(true);
        setPreviewError(false);

        try {
            const res = await patientService.previewPatientArticleEmail(clinicId, patient.id, buildPayload());
            setPreviewHtml(res.data.html || '');
            setPreviewSubject(res.data.subject || '');
        } catch (error) {
            console.error('Failed to preview article email', error);
            setPreviewHtml('');
            setPreviewSubject('');
            setPreviewError(true);
            showError({
                title: 'Preview Failed',
                text: error?.response?.data?.errorMessage || 'Could not generate the article email preview.',
            });
        } finally {
            setPreviewLoading(false);
        }
    }, [clinicId, patient?.id, selectedArticleIds, buildPayload]);

    useEffect(() => {
        if (!show) {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            loadPreview();
        }, PREVIEW_DEBOUNCE_MS);

        return () => window.clearTimeout(timeoutId);
    }, [show, loadPreview, selectedArticleIds, customMessage]);

    const handleSend = async () => {
        if (!clinicId || !patient?.id || selectedArticleIds.length === 0) {
            return;
        }

        setSending(true);

        try {
            await patientService.sendPatientArticleEmail(clinicId, patient.id, buildPayload());
            showSuccess({
                title: 'Email Queued',
                text: `Article email to ${patient.email} has been queued for delivery.`,
            });
            onClose();
        } catch (error) {
            console.error('Failed to send article email', error);
            showError({
                title: 'Send Failed',
                text: error?.response?.data?.errorMessage || 'Could not queue the article email for sending.',
            });
        } finally {
            setSending(false);
        }
    };

    const selectedArticles = selectedArticleIds
        .map((id) => selectedArticlesById[id])
        .filter(Boolean);

    const hasPreview = Boolean(previewHtml);
    const canSend = selectedArticleIds.length > 0 && !previewLoading && !previewError && hasPreview;

    if (!patient) {
        return null;
    }

    return (
        <Modal
            show={show}
            onHide={onClose}
            centered
            size="xl"
            scrollable
            backdrop="static"
            className="patient-send-articles-modal patient-send-email-modal"
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="bi bi-file-text me-2" />
                    Send Articles
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="patient-send-email-modal__meta mb-3">
                    <div className="patient-send-email-modal__meta-item">
                        <span className="patient-send-email-modal__meta-label">Patient</span>
                        <span className="patient-send-email-modal__meta-value">{patientLabel}</span>
                    </div>
                    <div className="patient-send-email-modal__meta-item">
                        <span className="patient-send-email-modal__meta-label">To</span>
                        <span className="patient-send-email-modal__meta-value">{patient.email}</span>
                    </div>
                </div>

                <div className="row g-3">
                    <div className="col-lg-5">
                        <div className="patient-send-articles-modal__picker">
                            <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                                <h6 className="mb-0 fw-normal">Select Articles</h6>
                                {selectedArticleIds.length > 0 && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-link text-decoration-none p-0"
                                        onClick={clearSelection}
                                    >
                                        Clear ({selectedArticleIds.length})
                                    </button>
                                )}
                            </div>

                            <div className="d-flex flex-column gap-2 mb-3">
                                <form onSubmit={handleSearchSubmit} className="d-flex gap-2">
                                    <Form.Control
                                        type="search"
                                        size="sm"
                                        placeholder="Search articles..."
                                        value={searchTitle}
                                        onChange={(event) => setSearchTitle(event.target.value)}
                                    />
                                    <Button type="submit" variant="outline-secondary" size="sm">
                                        Search
                                    </Button>
                                </form>

                                <Form.Select
                                    size="sm"
                                    value={categoryId}
                                    onChange={handleCategoryChange}
                                    aria-label="Filter by category"
                                >
                                    <option value={0}>All categories</option>
                                    {flatCategories.map((category) => (
                                        <option key={category.id} value={category.id}>
                                            {`${'— '.repeat(category.depth)}${category.title}`}
                                        </option>
                                    ))}
                                </Form.Select>
                            </div>

                            {selectedArticles.length > 0 && (
                                <div className="patient-send-articles-modal__selected mb-3">
                                    {selectedArticles.map((article) => (
                                        <span key={article.id} className="patient-send-articles-modal__chip">
                                            {article.title}
                                            <button
                                                type="button"
                                                className="patient-send-articles-modal__chip-remove"
                                                onClick={() => toggleArticleSelection(article)}
                                                aria-label={`Remove ${article.title}`}
                                            >
                                                <i className="bi bi-x" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}

                            <div className="patient-send-articles-modal__list">
                                {articlesLoading ? (
                                    <div className="patient-send-articles-modal__loading">
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Loading articles...
                                    </div>
                                ) : articles.length === 0 ? (
                                    <p className="text-muted small mb-0">No articles found.</p>
                                ) : (
                                    articles.map((article) => {
                                        const isSelected = selectedArticleIds.includes(article.id);
                                        const isInactive = article.status === false;

                                        return (
                                            <label
                                                key={article.id}
                                                className={`patient-send-articles-modal__item${
                                                    isSelected ? ' is-selected' : ''
                                                }${isInactive ? ' is-disabled' : ''}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input flex-shrink-0 mt-1"
                                                    checked={isSelected}
                                                    disabled={isInactive}
                                                    onChange={() => toggleArticleSelection(article)}
                                                />
                                                <span className="patient-send-articles-modal__item-copy">
                                                    <span className="patient-send-articles-modal__item-title">
                                                        {article.title}
                                                    </span>
                                                    {article.category && (
                                                        <span className="patient-send-articles-modal__item-meta">
                                                            {article.category}
                                                        </span>
                                                    )}
                                                    {isInactive && (
                                                        <span className="patient-send-articles-modal__item-meta text-danger">
                                                            Inactive
                                                        </span>
                                                    )}
                                                </span>
                                            </label>
                                        );
                                    })
                                )}
                            </div>

                            {totalElements > 0 && (
                                <div className="patient-send-articles-modal__pagination">
                                    <Pagination
                                        currentPage={pageNo}
                                        totalPages={totalPages}
                                        totalElements={totalElements}
                                        pageSize={PAGE_SIZE}
                                        onPageChange={setPageNo}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="col-lg-7">
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-normal">Custom Message</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={customMessage}
                                onChange={(event) => setCustomMessage(event.target.value)}
                                maxLength={2000}
                            />
                        </Form.Group>

                        <div className="patient-send-email-modal__preview">
                            <div className="patient-send-email-modal__preview-header">
                                <h6 className="mb-1 fw-normal">Preview</h6>
                                <span className="text-muted small">
                                    {selectedArticleIds.length === 0
                                        ? 'Select at least one article to preview'
                                        : previewSubject || 'Subject preview'}
                                </span>
                            </div>

                            <div className="patient-send-email-modal__preview-body">
                                {selectedArticleIds.length === 0 ? (
                                    <p className="text-muted mb-0 small">
                                        Choose one or more articles from the list to generate an email preview.
                                    </p>
                                ) : previewLoading ? (
                                    <div className="patient-send-email-modal__preview-loading">
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Loading preview...
                                    </div>
                                ) : previewHtml ? (
                                    <div
                                        className="patient-send-email-modal__preview-html"
                                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                                    />
                                ) : (
                                    <p className="text-muted mb-0 small">No preview available.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onClose} disabled={sending}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    type="button"
                    disabled={sending || !canSend}
                    onClick={handleSend}
                >
                    {sending ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Sending...
                        </>
                    ) : (
                        <>
                            <i className="bi bi-send me-1" />
                            Send Email
                            {selectedArticleIds.length > 0 ? ` (${selectedArticleIds.length})` : ''}
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default SendPatientArticlesModal;
